import { useState, useEffect } from 'react'
import { createFileRoute, useSearch } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { XamseenMark } from '@/components/xamseen-logo'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: 'Unsubscribe — Xamseen' },
      { name: 'description', content: 'Manage your Xamseen email preferences.' },
    ],
  }),
})

function UnsubscribePage() {
  const search = useSearch({ from: '/unsubscribe' })
  const token = (search as { token?: string }).token
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'success' | 'error'>('loading')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }

    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.valid) {
          setStatus('valid')
        } else if (data.reason === 'already_unsubscribed') {
          setStatus('success')
        } else {
          setStatus('invalid')
        }
      })
      .catch(() => setStatus('error'))
  }, [token])

  const handleUnsubscribe = async () => {
    if (!token) return
    setIsSubmitting(true)
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      if (data.success || data.reason === 'already_unsubscribed') {
        setStatus('success')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-6 flex justify-center">
          <XamseenMark className="h-12 w-12" />
        </div>
        <h1 className="text-center font-display text-2xl font-bold text-foreground">
          Email preferences
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Manage the emails you receive from Xamseen.
        </p>

        <div className="mt-8">
          {status === 'loading' && (
            <p className="text-center text-sm text-muted-foreground">Checking your link...</p>
          )}
          {status === 'invalid' && (
            <p className="text-center text-sm text-destructive">
              This unsubscribe link is invalid or has expired.
            </p>
          )}
          {status === 'valid' && (
            <div className="space-y-4">
              <p className="text-center text-sm text-foreground">
                Click below to unsubscribe from Xamseen marketing and notification emails.
              </p>
              <Button
                className="w-full"
                onClick={handleUnsubscribe}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Confirming...' : 'Confirm unsubscribe'}
              </Button>
            </div>
          )}
          {status === 'success' && (
            <p className="text-center text-sm text-foreground">
              You&apos;ve been unsubscribed. You can update this anytime in Settings → Notifications.
            </p>
          )}
          {status === 'error' && (
            <p className="text-center text-sm text-destructive">
              Something went wrong. Please try again or contact support.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
